import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../utils/api';
import Header from '../components/Header';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { BookOpen, Play, FileText, MessageCircle, CheckCircle, Clock, Award } from 'lucide-react';
import { toast } from 'sonner';

const CourseDetailPage = ({ onMenuClick }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCourseDetail();
  }, [id]);

  const fetchCourseDetail = async () => {
    try {
      const response = await api.get(`/courses/${id}`);
      setCourse(response.data);
    } catch (error) {
      console.error('Error fetching course:', error);
      toast.error('Erreur lors du chargement du cours');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600"></div>
      </div>
    );
  }

  if (!course) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
        <Header title="Cours" onMenuClick={onMenuClick} />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Card>
            <CardContent className="p-12 text-center">
              <p className="text-slate-600 dark:text-slate-400">Cours non trouvé</p>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  const formatCourseDuration = () => {
    const lessonMinutes = (course.modules || []).reduce((total, module) => {
      return total + (module.lessons || []).reduce(
        (sum, lesson) => sum + (parseInt(lesson.duration_minutes, 10) || 0),
        0
      );
    }, 0);

    if (lessonMinutes > 0) {
      const hours = Math.floor(lessonMinutes / 60);
      const minutes = lessonMinutes % 60;
      if (hours > 0 && minutes > 0) return `${hours}h ${minutes}min de formation`;
      if (hours > 0) return `${hours}h de formation`;
      return `${lessonMinutes} min de formation`;
    }

    if (course.duration_calculated?.formatted) {
      return `${course.duration_calculated.formatted} de formation`;
    }

    if (course.duration_hours) {
      return `${course.duration_hours}h de formation`;
    }

    return null;
  };

  const durationLabel = formatCourseDuration();

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 overflow-x-hidden">
      <Header title={course.name} onMenuClick={onMenuClick} />
      
      <main className="max-w-7xl mx-auto w-full px-3 sm:px-6 lg:px-8 py-4 sm:py-8 pb-24 sm:pb-8">
        {/* Course Header */}
        <div className="mb-6 sm:mb-8">
          <div className="flex flex-col sm:flex-row items-start gap-4 sm:gap-6 mb-6">
            <div className="w-14 h-14 sm:w-20 sm:h-20 rounded-xl bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center flex-shrink-0">
              <BookOpen className="w-7 h-7 sm:w-10 sm:h-10 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="text-xl sm:text-3xl font-bold text-slate-900 dark:text-white mb-2 break-words">
                {course.name}
              </h1>
              {course.description && (
                <p className="text-sm sm:text-base text-slate-600 dark:text-slate-400 mb-4">
                  {course.description}
                </p>
              )}
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs sm:text-sm text-slate-600 dark:text-slate-400">
                <div className="flex items-center gap-2">
                  <BookOpen className="w-4 h-4 shrink-0" />
                  <span>{course.modules?.length || 0} modules</span>
                </div>
                {durationLabel && (
                  <div className="flex items-center gap-2 min-w-0">
                    <Clock className="w-4 h-4 shrink-0" />
                    <span className="truncate">{durationLabel}</span>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <Award className="w-4 h-4 shrink-0" />
                  <span>Certification incluse</span>
                </div>
              </div>
            </div>
          </div>

          {/* Progress Bar */}
          <Card>
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center justify-between mb-2 gap-2">
                <span className="text-sm font-medium text-slate-900 dark:text-white">Progression du cours</span>
                <span className="text-sm font-bold text-violet-600 shrink-0">65%</span>
              </div>
              <div className="w-full h-3 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-violet-600 to-indigo-600" style={{ width: '65%' }}></div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Course Content Tabs */}
        <Tabs defaultValue="modules" className="space-y-6">
          <TabsList className="grid w-full max-w-2xl grid-cols-4 h-auto gap-1 p-1">
            <TabsTrigger value="modules" data-testid="modules-tab" className="flex-col sm:flex-row gap-1 px-1 sm:px-3 py-2 text-[10px] sm:text-sm">
              <BookOpen className="w-4 h-4 sm:mr-1" />
              <span>Modules</span>
            </TabsTrigger>
            <TabsTrigger value="resources" data-testid="resources-tab" className="flex-col sm:flex-row gap-1 px-1 sm:px-3 py-2 text-[10px] sm:text-sm">
              <FileText className="w-4 h-4 sm:mr-1" />
              <span className="truncate">Ressources</span>
            </TabsTrigger>
            <TabsTrigger value="evaluations" data-testid="evaluations-tab" className="flex-col sm:flex-row gap-1 px-1 sm:px-3 py-2 text-[10px] sm:text-sm">
              <Award className="w-4 h-4 sm:mr-1" />
              <span className="truncate">Évaluations</span>
            </TabsTrigger>
            <TabsTrigger value="discussion" data-testid="discussion-tab" className="flex-col sm:flex-row gap-1 px-1 sm:px-3 py-2 text-[10px] sm:text-sm">
              <MessageCircle className="w-4 h-4 sm:mr-1" />
              <span className="truncate">Discussion</span>
            </TabsTrigger>
          </TabsList>

          {/* Modules Tab */}
          <TabsContent value="modules">
            <div className="space-y-4">
              {course.modules && course.modules.length > 0 ? (
                course.modules.map((module, index) => (
                  <Card key={module.id} data-testid={`module-card-${module.id}`}>
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-lg">
                          {module.name}
                        </CardTitle>
                        <span className="text-sm text-slate-500">{module.lessons?.length || 0} leçons</span>
                      </div>
                      {module.description && (
                        <p className="text-sm text-slate-600 dark:text-slate-400 mt-2">{module.description}</p>
                      )}
                    </CardHeader>
                    <CardContent>
                      {module.lessons && module.lessons.length > 0 ? (
                        <div className="space-y-2">
                          {module.lessons.map((lesson) => (
                            <div
                              key={lesson.id}
                              className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors cursor-pointer"
                              data-testid={`lesson-item-${lesson.id}`}
                            >
                              <div className="w-8 h-8 rounded-lg bg-violet-100 dark:bg-violet-900/20 flex items-center justify-center flex-shrink-0">
                                {lesson.lesson_type === 'video' ? (
                                  <Play className="w-4 h-4 text-violet-600" />
                                ) : (
                                  <FileText className="w-4 h-4 text-violet-600" />
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <h4 className="font-medium text-slate-900 dark:text-white text-sm">{lesson.name}</h4>
                                {lesson.duration_minutes && (
                                  <p className="text-xs text-slate-500">{lesson.duration_minutes} min</p>
                                )}
                              </div>
                              <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-slate-500 text-center py-4">Aucune leçon disponible</p>
                      )}
                    </CardContent>
                  </Card>
                ))
              ) : (
                <Card>
                  <CardContent className="p-12 text-center">
                    <BookOpen className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                    <p className="text-slate-600 dark:text-slate-400">Aucun module disponible</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* Resources Tab */}
          <TabsContent value="resources">
            <Card>
              <CardContent className="p-12 text-center">
                <FileText className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                <p className="text-slate-600 dark:text-slate-400 mb-4">Ressources du cours</p>
                <p className="text-sm text-slate-500">Les documents et ressources complémentaires apparaîtront ici</p>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Evaluations Tab */}
          <TabsContent value="evaluations">
            <Card>
              <CardContent className="p-12 text-center">
                <Award className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                <p className="text-slate-600 dark:text-slate-400 mb-4">Évaluations du cours</p>
                <Button onClick={() => navigate('/evaluations')}>
                  Accéder aux évaluations
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Discussion Tab */}
          <TabsContent value="discussion">
            <Card>
              <CardContent className="p-12 text-center">
                <MessageCircle className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                <p className="text-slate-600 dark:text-slate-400 mb-4">Discussion du cours</p>
                <Button onClick={() => navigate('/forum')}>
                  Accéder au forum
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default CourseDetailPage;
